// ─── Input Constraints ────────────────────────────────────────────────────────
export const INPUT_LIMITS = {
  QUERY_MAX_LENGTH: 500,
  TARGET_MAX_LENGTH: 100,
  ALLOWED_DEPTHS: [5, 10, 15, 20],
  DEFAULT_DEPTH: 10,
  MAX_RESPONSE_BYTES: 50_000,   // 50KB cap on AI response
  REQUEST_TIMEOUT_MS: 30_000,   // 30s timeout
  RATE_LIMIT_MS: 3_000,         // min 3s between requests
};

// ─── Whitelisted risk/category values from AI ─────────────────────────────────
const ALLOWED_RISK = new Set(["high", "medium", "low"]);
const ALLOWED_CATEGORIES = new Set([
  "exposed_docs", "login_portals", "open_dirs",
  "cameras_iot", "secrets", "general",
]);

// ─── V-03: Input sanitization & length limiting ───────────────────────────────
/**
 * Sanitize a user text input:
 *   - Strip control characters (except newline/tab)
 *   - Trim whitespace
 *   - Enforce max length
 *   - Remove null bytes
 */
export function sanitizeText(value, maxLength) {
  if (typeof value !== "string") return "";
  return value
    .replace(/\0/g, "")                         // null bytes
    .replace(/[\x01-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // control chars (keep \t \n)
    .trim()
    .slice(0, maxLength);
}

// ─── V-08: Safe depth parsing ─────────────────────────────────────────────────
export function safeDepth(value) {
  const n = parseInt(value, 10);
  if (INPUT_LIMITS.ALLOWED_DEPTHS.includes(n)) return n;
  return INPUT_LIMITS.DEFAULT_DEPTH;
}

// ─── V-05: Whitelist AI-returned enum values ──────────────────────────────────
export function safeRisk(value) {
  if (typeof value === "string" && ALLOWED_RISK.has(value.toLowerCase())) {
    return value.toLowerCase();
  }
  return "low";
}

export function safeCategory(value) {
  if (typeof value === "string" && ALLOWED_CATEGORIES.has(value.toLowerCase())) {
    return value.toLowerCase();
  }
  return "general";
}

// ─── Sanitize a single string field from AI response ─────────────────────────
export function safeString(value, maxLength = 500) {
  if (typeof value !== "string") return "";
  return value.replace(/\0/g, "").slice(0, maxLength);
}

// ─── Sanitize a string array from AI response ────────────────────────────────
export function safeStringArray(value, maxItems = 5, maxItemLength = 300) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((v) => typeof v === "string")
    .slice(0, maxItems)
    .map((v) => safeString(v, maxItemLength));
}

// ─── V-05: Fully validate and sanitize a dork object returned by AI ──────────
export function sanitizeDork(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    query:            safeString(raw.query, 300),
    description:      safeString(raw.description, 400),
    category:         safeCategory(raw.category),
    risk:             safeRisk(raw.risk),
    operators:        safeStringArray(raw.operators, 10, 20),
    sensitivity_note: safeString(raw.sensitivity_note, 300),
    variations:       safeStringArray(raw.variations, 3, 300),
    stealth_note:     safeString(raw.stealth_note, 300),
  };
}

// ─── V-07: Simple rate limiter ────────────────────────────────────────────────
let lastRequestTime = 0;

export function checkRateLimit() {
  const now = Date.now();
  const elapsed = now - lastRequestTime;
  if (elapsed < INPUT_LIMITS.RATE_LIMIT_MS) {
    const wait = Math.ceil((INPUT_LIMITS.RATE_LIMIT_MS - elapsed) / 1000);
    throw new Error(`Please wait ${wait}s before generating again.`);
  }
  lastRequestTime = now;
}

// ─── V-09: Map internal API errors to safe user-facing messages ──────────────
export function safeErrorMessage(err) {
  const msg = typeof err?.message === "string" ? err.message : "";

  if (msg.includes("rate_limit") || msg.includes("429"))
    return "API rate limit reached. Please wait a moment and try again.";
  if (msg.includes("401") || msg.includes("auth") || msg.includes("api_key"))
    return "Authentication error. Check your API configuration.";
  if (msg.includes("timeout") || msg.includes("AbortError"))
    return "Request timed out. Check your connection and try again.";
  if (msg.includes("quota") || msg.includes("billing"))
    return "API quota exceeded. Check your account limits.";
  if (msg.includes("Please wait"))
    return msg; // safe, our own message

  // Generic fallback — never expose raw API internals
  return "Generation failed. Please try again.";
}
